import { useGetExerciseOptions } from '../../../../redux/api/exerciseConfigAPI';
import { IProgramSet } from '../../../../types/interfaces/program-set';
import { IProgramSetItem } from '../../../../types/interfaces/program-set-item';
import { IProgramSetItemOption } from '../../../../types/interfaces/program-set-item-option';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../../../types/interfaces/training-set-item-option';
import { changeSetItemOrder } from '../../../../utils/training/set-utils';
import { IUseSetGroupEditTable, SetGroupFieldName } from '../interfaces';

export interface IUseSetItemEdit {
  onRemoveSetItem: (itemForRemove: IProgramSetItem | ITrainingSetItem) => void;
  onChangeSetItem: (setItem: IProgramSetItem | ITrainingSetItem) => void;
  onChangeSetItemOrder: (
    oldOrderNumber: number,
    newOrderNumber: number,
  ) => void;
  onChangeSetItemOption: (
    setItem: IProgramSetItem | ITrainingSetItem,
    option: IProgramSetItemOption | ITrainingSetItemOption,
  ) => void;
}

export function useSetItemEdit(
  useSetGroupEditTable: IUseSetGroupEditTable<IProgramSet | ITrainingSet>,
  set: IProgramSet | ITrainingSet,
  setItem: IProgramSetItem | ITrainingSetItem,
) {
  const { data: exerciseOptions } = useGetExerciseOptions();
  const { mode, onChangeSet, onRemoveSetsBySetNumber } = useSetGroupEditTable;

  function onRemoveSetItem() {
    const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;
    const updatedSet = { ...set };

    updatedSet[setItemsFieldName] = updatedSet[setItemsFieldName]
      .map((item) => {
        if (item.orderNumber < setItem.orderNumber) return item;

        if (item.orderNumber === setItem.orderNumber) return null;

        return { ...item, orderNumber: item.orderNumber - 1 };
      })
      .filter(Boolean);

    if (updatedSet[setItemsFieldName]?.length === 0) {
      onRemoveSetsBySetNumber(set.setNumber, set.orderNumber);
    } else {
      onChangeSet(updatedSet);
    }
  }

  function onChangeSetItemOrder(
    oldOrderNumber: number,
    newOrderNumber: number,
  ) {
    const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;
    const setItems = set[setItemsFieldName];

    const updatedProgramSetItems = changeSetItemOrder(
      setItems,
      oldOrderNumber,
      newOrderNumber,
    );

    const updatedSet = {
      ...set,
      [setItemsFieldName]: updatedProgramSetItems,
    };

    onChangeSet(updatedSet);
  }

  function onChangeSetItem(
    update: Partial<IProgramSetItem | ITrainingSetItem>,
  ) {
    const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;
    const updatedSet = { ...set };

    updatedSet[setItemsFieldName] = updatedSet[setItemsFieldName].map(
      (item) => {
        if (setItem.orderNumber === item.orderNumber)
          return { ...item, ...update };
        return item;
      },
    );

    onChangeSet(updatedSet);
  }

  function onChangeSetItemOption(
    option: IProgramSetItemOption | ITrainingSetItemOption,
  ) {
    const setItemOptionsFieldName = SetGroupFieldName[mode].optionsFieldName;
    const updatedOptions = setItem[setItemOptionsFieldName].map((o) => {
      if (o.optionId === option.optionId) return { ...o, ...option };
      return o;
    });

    onChangeSetItem({ [setItemOptionsFieldName]: updatedOptions });
  }

  return {
    exerciseOptions,
    onRemoveSetItem,
    onChangeSetItem,
    onChangeSetItemOrder,
    onChangeSetItemOption,
  };
}
